import { useCallback, useEffect, useState } from "react";
import type { AuthConfig } from "@fmc-digital/core-auth";
import type { i18n } from "i18next";

import { getAppInitPromise, resetAppInitPromise } from "../initialization";

export interface UseAppInitializationResult {
  authConfig: AuthConfig | null;
  i18nInstance: i18n | null;
  isLoading: boolean;
  initError: Error | null;
  retry: () => void;
}

/**
 * Subscribes to app initialization (shared promise so StrictMode double-mount
 * still receives the result) and returns auth config, i18n instance, loading state,
 * error state, and a retry callback.
 */
export function useAppInitialization(): UseAppInitializationResult {
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [i18nInstance, setI18nInstance] = useState<i18n | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const retry = useCallback(() => {
    resetAppInitPromise();
    setInitError(null);
    setAuthConfig(null);
    setI18nInstance(null);
    setIsLoading(true);
    setRetryCount((c) => c + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    getAppInitPromise()
      .then((result) => {
        if (cancelled) return;
        if (result) {
          setAuthConfig(result.authConfig);
          setI18nInstance(result.i18nInstance);
        } else {
          setInitError(new Error("Application initialization failed"));
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to initialize app:", error);
          setInitError(
            error instanceof Error ? error : new Error(String(error))
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [retryCount]);

  return { authConfig, i18nInstance, isLoading, initError, retry };
}
